import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { AuthShell, PasswordRules, strongPassword } from "../../components/admin/AuthShell";
import { FormAlert, inputClass } from "../../components/forms/FormKit";

const Done = ({ message, testId }) => (
  <div role="status" data-testid={testId}>
    <CheckCircle2 className="h-9 w-9 text-[#2563EB]" strokeWidth={1.5} />
    <p className="mt-6 text-sm leading-relaxed text-slate-600">{message}</p>
    <Link to="/admin/login" className="btn-primary mt-8 w-full" data-testid="set-password-login-link">Sign in</Link>
  </div>
);

// mode: "reset" (password recovery) or "invite" (accept admin invitation)
export default function SetPassword({ mode = "reset" }) {
  const token = useSearchParams()[0].get("token") || "";
  const invite = mode === "invite";
  const [info, setInfo] = useState(invite ? null : {});
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(token ? "" : "This link is missing its security token.");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!invite || !token) return;
    api.post("/auth/invitations/verify", { token })
      .then(({ data }) => { setInfo(data); setName(data.name); })
      .catch((e) => setError(formatApiError(e)));
  }, [invite, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!strongPassword(pw)) return setError("Password must be at least 12 characters and include letters and numbers.");
    if (pw !== pw2) return setError("Passwords do not match.");
    if (invite && name.trim().length < 2) return setError("Enter your name.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(invite ? "/auth/invitations/accept" : "/auth/reset-password", invite ? { token, name, password: pw } : { token, password: pw });
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const title = invite ? "Accept invitation" : "Choose a new password";
  return (
    <AuthShell title={title}>
      {done ? <Done message={done} testId={invite ? "accept-invite-success" : "reset-password-success"} /> : (
        <form onSubmit={submit} noValidate data-testid={invite ? "accept-invite-form" : "reset-password-form"}>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0B1220]">{title}</h1>
          {invite && info && <p className="mt-2 text-sm text-slate-500" data-testid="invite-details">You have been invited as <strong>{info.role}</strong> ({info.email}).</p>}
          {!info && !error && <Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" />}
          {info && token && (
            <div className="mt-8 space-y-5">
              {invite && (
                <div>
                  <label htmlFor="sp-name" className="mb-2 block text-sm font-medium text-[#111827]">Your name</label>
                  <input id="sp-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass(false)} data-testid="set-password-name-input" />
                </div>
              )}
              <div>
                <label htmlFor="sp-pw" className="mb-2 block text-sm font-medium text-[#111827]">New password</label>
                <input id="sp-pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputClass(false)} data-testid="set-password-input" />
                <div className="mt-1.5"><PasswordRules /></div>
              </div>
              <div>
                <label htmlFor="sp-pw2" className="mb-2 block text-sm font-medium text-[#111827]">Confirm password</label>
                <input id="sp-pw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputClass(false)} data-testid="set-password-confirm-input" />
              </div>
            </div>
          )}
          <div className="mt-5"><FormAlert message={error} testId="set-password-error" /></div>
          {info && token && (
            <button type="submit" disabled={busy} className="btn-primary mt-5 w-full" data-testid="set-password-submit-button">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving</> : invite ? "Create account" : "Update password"}
            </button>
          )}
          <Link to={invite ? "/admin/login" : "/admin/forgot-password"} className="mt-6 block text-center text-sm text-slate-500 hover:text-[#0B1220]" data-testid="set-password-alt-link">{invite ? "Back to sign in" : "Request a new link"}</Link>
        </form>
      )}
    </AuthShell>
  );
}
